import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type Category, type PlanMode, type Transaction } from '../db/database'
import { loadSummary, setEnvelope, withdrawFromSavings, type Envelope } from '../db/budget'
import { writeLocal } from '../db/sync'
import { useCycleLocked } from '../lib/lock'
import { cycleLabel, cycleRange } from '../lib/cycles'
import { formatAmount, formatAriary, parseAmountBounded } from '../lib/money'
import { today } from '../lib/dates'
import { Icon, type IconName } from '../components/Icon'
import { PlanDaysSheet } from '../components/PlanDaysSheet'
import { EnvelopeHistorySheet } from '../components/EnvelopeHistory'
import { GoalsSection } from '../components/GoalsSection'
import { CycleReportSheet } from '../components/CycleReportSheet'
import './BudgetScreen.css'

/**
 * La répartition : poser combien va dans chaque enveloppe.
 *
 * L'écran affiche DEUX montants qu'il ne faut jamais confondre :
 *
 *   « à placer » — ce qui n'a pas encore de destination. C'est le seul des deux
 *                  qui appelle une action, donc le seul à passer en gros, et
 *                  seulement tant qu'il n'est pas à zéro.
 *   « en poche » — ce que l'utilisateur peut recompter en billets. Toujours
 *                  présent, jamais en vedette : c'est un fait à vérifier, pas
 *                  une décision à prendre.
 *
 * Afficher un seul des deux est faux dans les deux sens : « 0 » ferait croire à
 * l'utilisateur qu'il est ruiné alors qu'il a de quoi vivre, et le solde seul
 * lui ferait dépenser l'argent déjà réservé au loyer.
 */
export function BudgetScreen(
  { walletId, onOpen }: {
    walletId: string
    /** Ouvrir le détail d'une dépense, depuis l'historique d'une enveloppe. */
    onOpen?: (transaction: Transaction) => void
  },
) {
  // Répartir pendant qu'une clôture attend, c'est décider du cycle qui commence
  // sur des enveloppes que la clôture est en train de solder.
  const locked = useCycleLocked()
  // Le brouillon de saisie vit ici et non en base : tant que l'utilisateur
  // tape, on n'écrit rien. Écrire à chaque frappe ferait partir une opération
  // de synchronisation par caractère.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [planning, setPlanning] = useState<{ category: Category; envelope: Envelope } | null>(null)
  // « Où est passé l'argent de cette enveloppe ? » — la même question quel que
  // soit le mode de dépense, donc un écran disponible sur TOUTES les enveloppes
  // et pas seulement sur celles qui se planifient jour par jour.
  const [history, setHistory] = useState<{ category: Category; envelope: Envelope } | null>(null)
  // Le bilan du cycle : une feuille et non une cinquième destination — la
  // navigation est arrêtée à quatre (décision fermée).
  const [reporting, setReporting] = useState(false)
  const [withdrawing, setWithdrawing] = useState<string | null>(null)
  const [withdrawal, setWithdrawal] = useState('')
  /*
   * Le glisser-déposer.
   *
   * Trois règles, apprises en le ratant :
   *
   * 1. LES ÉCOUTEURS VIVENT SUR `window`, jamais sur le bouton.
   *    `setPointerCapture` échoue silencieusement dans plusieurs situations, et
   *    sans capture une poignée de 34 px cesse de recevoir `pointermove` dès que
   *    le doigt en sort — c'est-à-dire immédiatement.
   *
   * 2. ON NE RÉORDONNE PAS LA LISTE PENDANT LE GESTE. La ligne tenue reçoit un
   *    `translateY` égal au déplacement du doigt, et les autres s'écartent de la
   *    hauteur de la ligne pour ouvrir la place. Réordonner le tableau ET
   *    translater faisait sauter la carte : la translation était mesurée depuis
   *    la position de DÉPART, alors que la ligne venait de changer d'index, donc
   *    de position de mise en page.
   *
   * 3. TOUT SE MESURE EN COORDONNÉES DE CONTENU, défilement compris. Sans ça,
   *    déplacer près d'un bord fait défiler la liste sous la carte, et la carte
   *    décroche du doigt.
   */
  const [drag, setDrag] = useState<Drag | null>(null)
  const dragRef = useRef<Drag | null>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const scrollerRef = useRef<HTMLElement | null>(null)
  const geometry = useRef<Geometry | null>(null)
  const pointerY = useRef(0)
  /** L'ordre au moment où l'on a saisi la ligne — les écouteurs `window` ne
      verraient qu'une version périmée de celui du rendu. */
  const orderAtStart = useRef<Category[]>([])
  /**
   * L'ordre choisi, appliqué DÈS LE LÂCHER sans attendre Dexie.
   *
   * Sans lui, on retirait la translation avant que la liste ne se réordonne :
   * la carte revenait une fraction de seconde à sa place de départ, puis la
   * liste changeait sous elle. C'est le sursaut qu'on voyait au dépôt.
   */
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null)
  /** La carte à faire glisser jusqu'à sa place après le lâcher (voir plus bas). */
  const settling = useRef<{ id: string; top: number } | null>(null)

  function setDragState(next: Drag | null) {
    dragRef.current = next
    setDrag(next)
  }

  /*
   * Le glissement de fin (technique « FLIP »).
   *
   * Au lâcher, la liste se réordonne d'un coup : la carte se retrouve d'office à
   * sa nouvelle place, souvent à cent pixels de l'endroit où le doigt l'a
   * laissée. C'était le saut brutal qu'on voyait. On la remet donc d'abord là où
   * elle était — sans transition, invisible — puis on la laisse glisser jusqu'à
   * sa place.
   *
   * `useLayoutEffect` et non `useEffect` : la correction doit être posée AVANT
   * que le navigateur ne peigne, sinon on voit précisément le saut qu'on
   * cherche à supprimer.
   */
  useLayoutEffect(() => {
    const settle = settling.current
    const list = listRef.current
    if (!settle || !list) return
    settling.current = null

    const card = [...list.children].find(
      (child) => (child as HTMLElement).dataset.id === settle.id) as HTMLElement | undefined
    if (!card) return

    const delta = settle.top - card.getBoundingClientRect().top
    if (Math.abs(delta) < 1) return

    card.style.transition = 'none'
    card.style.transform = `translateY(${delta}px)`
    // Lecture forcée : sans elle le navigateur regroupe les deux écritures et
    // rien ne s'anime. On évite `requestAnimationFrame`, qui ne se déclenche
    // pas dans un onglet en arrière-plan.
    void card.offsetHeight
    card.style.transition = 'transform .18s ease'
    card.style.transform = ''

    const clean = () => {
      card.style.transition = ''
      card.removeEventListener('transitionend', clean)
    }
    card.addEventListener('transitionend', clean)
  })

  const dragging = drag !== null
  useEffect(() => {
    if (!dragging) return

    /** Recalcule le décalage de la carte et l'index visé. */
    function refresh() {
      const geo = geometry.current
      const current = dragRef.current
      if (!geo || !current) return

      const scrollTop = scrollerRef.current?.scrollTop ?? 0
      const raw = pointerY.current + scrollTop - geo.startDoc
      // Bornage : la carte ne sort pas de la liste. Continuer à tirer au-delà du
      // premier ou du dernier ne mène nulle part — autant que le geste s'arrête
      // là où il n'a plus d'effet, plutôt que de laisser croire le contraire.
      const held = geo.rows[current.from]
      const dy = Math.max(
        geo.rows[0].top - held.top,
        Math.min(geo.rows[geo.rows.length - 1].bottom - held.bottom, raw),
      )

      /*
       * La cible est la place dont l'ARRIVÉE serait la plus proche de là où la
       * carte se trouve. On ne compare pas un centre à des milieux de lignes :
       * avec des hauteurs inégales, une carte plus haute que la première ligne
       * ne pouvait JAMAIS atteindre le sommet — le bornage l'arrêtait avant que
       * son centre n'ait franchi ce milieu. Et les cartes grandissent dès qu'on
       * leur donne un montant (jauge, bouton de planification), si bien que le
       * défaut apparaissait à l'usage et pas au premier essai.
       *
       * Ici, au bornage haut la carte coïncide exactement avec la place 0, donc
       * la distance y vaut zéro : les deux extrémités sont toujours atteignables.
       */
      const height = held.bottom - held.top
      const cardTop = held.top + dy
      let to = current.from
      let best = Infinity
      for (let index = 0; index < geo.rows.length; index += 1) {
        const landing = index <= current.from
          ? geo.rows[index].top
          : geo.rows[index].bottom - height
        const distance = Math.abs(landing - cardTop)
        if (distance < best) { best = distance; to = index }
      }

      if (dy !== current.dy || to !== current.to) setDragState({ ...current, dy, to })
    }

    /*
     * Défilement automatique près des bords : sans lui, on ne peut pas déplacer
     * une enveloppe au-delà de ce qui tient à l'écran — et sur onze catégories,
     * c'est le cas la moitié du temps.
     */
    function autoScroll() {
      const scroller = scrollerRef.current
      if (!scroller) return
      const box = scroller.getBoundingClientRect()
      const y = pointerY.current
      let delta = 0
      if (y < box.top + EDGE) delta = -SCROLL_SPEED * Math.min(1, (box.top + EDGE - y) / EDGE)
      else if (y > box.bottom - EDGE) delta = SCROLL_SPEED * Math.min(1, (y - box.bottom + EDGE) / EDGE)
      if (delta === 0) return
      scroller.scrollTop += delta
      refresh()
    }

    let frame = requestAnimationFrame(function tick() {
      autoScroll()
      frame = requestAnimationFrame(tick)
    })

    const onMove = (event: PointerEvent) => {
      pointerY.current = event.clientY
      refresh()
    }
    const onEnd = () => {
      const current = dragRef.current
      geometry.current = null
      if (current && current.from !== current.to) {
        // On note où la carte se trouve VISUELLEMENT à l'instant du lâcher : elle
        // atterrit là où le doigt l'a laissée, pas forcément sur sa nouvelle
        // place. C'est de là qu'on la fera glisser.
        const card = listRef.current?.children[current.from] as HTMLElement | undefined
        if (card) {
          settling.current = {
            id: orderAtStart.current[current.from].id,
            top: card.getBoundingClientRect().top,
          }
        }
        const next = move(orderAtStart.current, current.from, current.to)
        // L'ordre d'abord, le relâchement ensuite : les deux dans le même rendu,
        // de sorte que la carte se retrouve déjà à sa place quand la
        // translation disparaît.
        setPendingOrder(next.map((category) => category.id))
        void persistOrder(walletId, next)
      }
      setDragState(null)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
    }
  }, [dragging, walletId])

  /*
   * La pastille flottante n'apparaît qu'une fois l'en-tête sorti de l'écran.
   *
   * Une sentinelle en haut du contenu, observée par IntersectionObserver : dès
   * qu'elle quitte la zone visible, on sait que l'en-tête est parti. On préfère
   * ça à un écouteur de défilement, qui se déclencherait des dizaines de fois
   * par geste pour la même information.
   *
   * Ref de RAPPEL et non `useRef` + `useEffect([])` : l'écran rend d'abord un
   * squelette le temps que Dexie réponde, si bien qu'un effet à dépendances
   * vides s'exécuterait alors que la sentinelle n'existe pas encore — et ne se
   * rejouerait jamais. La pastille ne serait apparue chez personne.
   */
  const observer = useRef<IntersectionObserver | null>(null)
  const [floating, setFloating] = useState(false)

  const sentinel = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect()
    if (!node || typeof IntersectionObserver === 'undefined') return
    observer.current = new IntersectionObserver(
      ([entry]) => setFloating(!entry.isIntersecting), { threshold: 0 })
    observer.current.observe(node)
  }, [])

  useEffect(() => () => observer.current?.disconnect(), [])

  /*
   * L'ordre optimiste ne s'efface que lorsque la base l'a VRAIMENT rattrapé.
   *
   * Il s'effaçait auparavant dès que l'écriture avait abouti — mais la requête
   * vive de Dexie ne s'était pas forcément déclenchée à cet instant : on
   * repassait alors une image sur l'ANCIEN ordre avant de recevoir le nouveau.
   * C'est ce qui faisait vibrer plusieurs lignes après le dépôt.
   */
  const data = useLiveQuery(async () => {
    const wallet = await db.wallets.get(walletId)
    const cycle = wallet?.cycle ?? 'monthly'
    const categories = (await db.categories.where({ wallet_id: walletId }).toArray())
      .filter((c) => !c.deleted_at && c.direction === 'expense')
      .sort((a, b) => a.position - b.position)
    return {
      summary: await loadSummary(walletId), categories, cycle,
      manualOrder: wallet?.manual_category_order ?? false,
    }
  }, [walletId])

  if (pendingOrder && data &&
      data.categories.map((c) => c.id).join('|') === pendingOrder.join('|')) {
    // Pendant le rendu et non dans un effet : React ré-exécute simplement ce
    // rendu-ci avec l'état à jour, sans jamais peindre l'image intermédiaire.
    setPendingOrder(null)
  }

  if (!data) return <div className="budget-loading" />

  const { summary, categories, cycle, manualOrder } = data
  const range = cycleRange(today(), cycle)
  const byCategory = new Map(summary.envelopes.map((e) => [e.category_id, e]))
  const allocated = summary.envelopes.reduce((sum, e) => sum + e.amount, 0)

  /*
   * Tant que l'utilisateur n'a rien rangé lui-même, les enveloppes sans montant
   * descendent en bas : ce qui n'est pas budgété n'a pas à occuper le haut de
   * l'écran, où se prend la décision. Dès qu'il glisse une ligne, son ordre
   * devient la vérité et on cesse de trier — un rangement fait à la main qui se
   * défait tout seul est pire que pas de rangement du tout.
   */
  const settled = manualOrder
    ? categories
    : [...categories].sort((a, b) => {
        const rank = (id: string) => ((byCategory.get(id)?.amount ?? 0) > 0 ? 0 : 1)
        return rank(a.id) - rank(b.id) || a.position - b.position
      })

  /*
   * L'ordre optimiste n'est retenu que s'il décrit EXACTEMENT les mêmes
   * catégories. Sinon on l'ignore : une catégorie ajoutée ou supprimée entre
   * temps disparaîtrait de l'écran sans que rien ne le signale.
   */
  const known = new Map(categories.map((category) => [category.id, category]))
  const optimistic = pendingOrder?.length === categories.length
    ? pendingOrder.map((id) => known.get(id))
    : undefined
  const ordered = optimistic?.every((c) => c !== undefined)
    ? (optimistic as Category[])
    : settled


  function startDrag(event: React.PointerEvent<HTMLButtonElement>, index: number) {
    const list = listRef.current
    if (!list) return
    event.preventDefault()

    const scroller = list.closest('.app-body') as HTMLElement | null
    scrollerRef.current = scroller
    const scrollTop = scroller?.scrollTop ?? 0

    // Géométrie figée au départ, en coordonnées de CONTENU (défilement inclus) :
    // les lignes ne changent pas de place pendant le geste, seule celle qu'on
    // tient bouge. Recalculer des rectangles qui se déplacent ferait osciller la
    // cible d'une ligne à l'autre.
    const rows = [...list.children].map((child) => {
      const box = (child as HTMLElement).getBoundingClientRect()
      return { top: box.top + scrollTop, bottom: box.bottom + scrollTop }
    })
    const gap = rows.length > 1 ? rows[1].top - rows[0].bottom : 0

    geometry.current = { rows, startDoc: event.clientY + scrollTop }
    pointerY.current = event.clientY
    orderAtStart.current = ordered
    setDragState({
      dy: 0, from: index, to: index,
      // La place libérée vaut exactement la hauteur de la ligne tenue plus
      // l'écart : c'est de ça que les autres s'écartent.
      shift: rows[index].bottom - rows[index].top + gap,
    })
  }

  /** Le décalage visuel d'une ligne pendant le geste. */
  function offsetOf(index: number): string | undefined {
    if (!drag) return undefined
    if (index === drag.from) return `translateY(${drag.dy}px)`
    if (drag.to > drag.from && index > drag.from && index <= drag.to) {
      return `translateY(${-drag.shift}px)`
    }
    if (drag.to < drag.from && index >= drag.to && index < drag.from) {
      return `translateY(${drag.shift}px)`
    }
    return undefined
  }

  /** Déplacement au clavier : le glisser-déposer seul exclut qui n'a pas de souris. */
  async function nudge(index: number, delta: number) {
    const to = index + delta
    if (to < 0 || to >= ordered.length) return
    await persistOrder(walletId, move(ordered, index, to))
  }



  async function commit(category: Category) {
    const draft = drafts[category.id]
    setDrafts((current) => {
      const { [category.id]: _, ...rest } = current
      return rest
    })
    if (draft === undefined) return

    const amount = draft.trim() === '' ? 0 : parseAmountBounded(draft)
    if (amount === null) return
    // Le cycle EN COURS se corrige sur place ; viser un cycle futur insèrerait
    // une nouvelle ligne et laisserait celui-ci intact.
    await setEnvelope({
      walletId, categoryId: category.id, amount, cycleStart: range.start,
      carriesOver: cumulParDefaut(category, byCategory.get(category.id)),
    })
  }

  /*
   * L'épargne est cumulative d'emblée : une enveloppe d'épargne remise à zéro
   * chaque cycle ne met jamais rien de côté, l'utilisateur réaffecterait les
   * mêmes 50 000 tous les mois en croyant épargner.
   *
   * Cette valeur par défaut vit ICI et nulle part ailleurs, parce qu'elle doit
   * valoir quel que soit le geste qui crée l'enveloppe. Elle n'était appliquée
   * qu'à la saisie du montant : cocher une option d'abord créait une enveloppe
   * non cumulative, et l'épargne perdait son comportement selon l'ORDRE des
   * gestes de l'utilisateur.
   *
   * Reconnue à l'icône de la catégorie d'amorçage — le jour où les catégories
   * deviendront modifiables, il faudra un vrai drapeau en base.
   */
  function cumulParDefaut(category: Category, envelope: Envelope | undefined): boolean {
    return envelope?.carries_over ?? category.icon === 'savings'
  }

  /** Le mode de dépense — au fil de l'eau, en une fois, ou jour par jour. */
  async function choosePlan(
    category: Category, envelope: Envelope | undefined, mode: PlanMode,
  ) {
    await setEnvelope({
      walletId, categoryId: category.id,
      amount: envelope?.amount ?? 0,
      cycleStart: range.start,
      planMode: mode,
      carriesOver: cumulParDefaut(category, envelope),
    })

    // « Jour par jour » ne veut rien dire tant que les journées ne sont pas
    // posées : on ouvre l'écran dans la foulée, plutôt que de laisser
    // l'utilisateur deviner qu'il reste un second bouton à trouver.
    if (mode === 'daily' && envelope && envelope.amount > 0) {
      setPlanning({ category, envelope: { ...envelope, plan_mode: 'daily' } })
    }
  }

  async function toggleCarry(
    category: Category, envelope: Envelope | undefined, value: boolean,
  ) {
    await setEnvelope({
      walletId, categoryId: category.id,
      amount: envelope?.amount ?? 0,
      cycleStart: range.start,
      carriesOver: value,
    })
  }

  return (
    <div className="budget">
      {/* Un pixel de repère, invisible : c'est sa sortie d'écran qui déclenche
          la pastille flottante. */}
      <div ref={sentinel} className="budget-sentinel" aria-hidden="true" />

      <header className="budget-head">
        <p className="budget-cycle">{cycleLabel(range, cycle)}</p>
      </header>

      {/* Le chiffre héros — mais seulement tant qu'il reste une décision à
          prendre. À zéro, il se retire et laisse la place aux enveloppes. */}
      {summary.toAllocate !== 0 ? (
        <section
          className={`to-allocate ${summary.toAllocate < 0 ? 'is-short' : ''}`}
          aria-labelledby="to-allocate-label"
        >
          <p className="to-allocate-label" id="to-allocate-label">
            {summary.toAllocate < 0 ? 'Il te manque' : 'À placer'}
          </p>
          <output className="to-allocate-amount tabular">
            {formatAriary(Math.abs(summary.toAllocate))}
          </output>
          <p className="to-allocate-help">
            {summary.toAllocate < 0
              ? 'Tu as réparti plus que ce que tu possèdes. Baisse une enveloppe, ou attends la prochaine rentrée d’argent.'
              : 'Cet argent n’a pas encore de destination. Pose-le dans une enveloppe.'}
          </p>
        </section>
      ) : (
        <p className="all-allocated">
          <Icon name="check" size={16} /> Tout est réparti
        </p>
      )}

      {/* Région nommée, comme sur l'accueil : un lecteur d'écran annonce
          l'intitulé avant le montant au lieu de lire un nombre isolé. */}
      <section className="in-pocket" aria-labelledby="in-pocket-label">
        <p id="in-pocket-label">En poche</p>
        <output className="tabular">{formatAriary(summary.inPocket)}</output>
      </section>

      {/*
        La pastille flottante : les deux mêmes nombres, une fois l'en-tête parti.
        C'est en RÉPARTISSANT qu'on en a besoin, et la liste des enveloppes tient
        rarement sur un écran.

        `aria-hidden` en permanence : c'est un doublon purement visuel des cartes
        ci-dessus. Sans ça, un lecteur d'écran annoncerait deux fois les mêmes
        montants, et l'utilisateur croirait à deux informations différentes.
      */}
      <div className={`budget-float ${floating ? 'is-visible' : ''}`} aria-hidden="true">
        <div className={`float-card ${summary.toAllocate < 0 ? 'is-short' : ''}`}>
          <span className="float-cycle">
            <b>Budget</b> {cycleLabel(range, cycle)}
          </span>
          <span className="float-figure is-lead">
            <em>{summary.toAllocate < 0 ? 'Il te manque' : 'À placer'}</em>
            <b className="tabular">{formatAriary(Math.abs(summary.toAllocate))}</b>
          </span>
          <span className="float-figure">
            <em>En poche</em>
            <b className="tabular">{formatAriary(summary.inPocket)}</b>
          </span>
        </div>
      </div>

      <section>
        <header className="section-head">
          <h2>Mes enveloppes</h2>
          <span className="budget-total tabular">{formatAmount(allocated)} réparti</span>
        </header>

        {/* Tout le bloc d'un coup : montants, poignées, options, retrait
            d'épargne. `fieldset disabled` couvre aussi ce qu'on y ajoutera. */}
        <fieldset className="bare" disabled={locked}>
          <ul className={`envelopes ${dragging ? 'is-dragging' : ''}`} ref={listRef}>
            {ordered.map((category, index) => {
              const envelope = byCategory.get(category.id)
              const draft = drafts[category.id]
              const value = draft ?? (envelope ? formatAmount(envelope.amount) : '')
              const filled = envelope ? envelope.accumulated - envelope.remaining : 0
              const ratio = envelope && envelope.accumulated > 0
                ? Math.min(1, filled / envelope.accumulated)
                : 0

              const held = drag !== null && index === drag.from
              const offset = offsetOf(index)
              return (
                <li
                  key={category.id}
                  data-id={category.id}
                  className={`envelope ${held ? 'is-held' : ''}`}
                  style={offset ? { transform: offset } : undefined}
                >
                  <div className="envelope-main">
                    <button
                      type="button"
                      className="envelope-grip"
                      aria-label={`Déplacer ${category.name}`}
                      onPointerDown={(event) => startDrag(event, index)}
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowUp') { event.preventDefault(); void nudge(index, -1) }
                        if (event.key === 'ArrowDown') { event.preventDefault(); void nudge(index, 1) }
                      }}
                    >
                      <Icon name="grip" size={16} />
                    </button>
                    <span className="envelope-icon" aria-hidden="true">
                      <Icon name={(category.icon ?? 'other') as IconName} size={20} />
                    </span>
                    <span className="envelope-name">{category.name}</span>
                    <span className="envelope-field">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="tabular"
                        value={value}
                        placeholder="0"
                        aria-label={`Montant pour ${category.name}`}
                        onChange={(event) =>
                          setDrafts((c) => ({ ...c, [category.id]: event.target.value }))}
                        onBlur={() => void commit(category)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur()
                        }}
                      />
                      <span aria-hidden="true">Ar</span>
                    </span>
                  </div>

                  {envelope && envelope.accumulated > 0 && (
                    <>
                      {/* Décoratif : l'information chiffrée est juste en dessous,
                          en toutes lettres. */}
                      <div className="envelope-gauge" aria-hidden="true">
                        <span
                          className={envelope.overspent ? 'is-over' : envelope.carries_over ? 'is-saving' : ''}
                          style={{ inlineSize: `${ratio * 100}%` }}
                        />
                      </div>
                      <p className={`envelope-state ${envelope.overspent ? 'is-over' : ''} ${envelope.carries_over ? 'is-saving' : ''}`}>
                        {envelope.carries_over
                          // Une épargne se lit à l'endroit : ce qui la remplit est
                          // un bien. « Dépassé » n'a aucun sens ici — ce serait
                          // gronder l'utilisateur d'avoir trop mis de côté.
                          ? envelope.remaining <= 0
                            ? `${formatAriary(envelope.spent)} mis de côté — objectif atteint`
                            : `${formatAriary(envelope.spent)} mis de côté sur ${formatAriary(envelope.accumulated)}`
                          : envelope.overspent
                            // Dire de combien ET pourquoi : « dépassé de 10 000 »
                            // tout seul n'explique rien et n'indique aucun geste.
                            ? `Dépassé de ${formatAriary(-envelope.remaining)} — ${formatAriary(envelope.spent)} dépensés dans une enveloppe de ${formatAriary(envelope.amount)}`
                            : `${formatAriary(envelope.remaining)} restants`}
                      </p>
                    </>
                  )}

                  {/* L'avancement du plan : combien de journées ont été
                      confirmées sur celles prévues. C'est le seul endroit où le
                      chiffre a un sens — sur chaque ligne d'historique il se
                      répéterait à l'identique sans rien apprendre. */}
                  {/* Retirer de l'épargne : le SEUL mouvement possible depuis
                      une enveloppe cumulative, et il ramène l'argent parmi celui
                      qu'on répartit. On ne dépense pas « depuis l'épargne ». */}
                  {envelope && envelope.carries_over && envelope.spent > 0 && (
                    withdrawing === category.id ? (
                      <div className="withdraw">
                        <label>
                          <span>Retirer</span>
                          <input type="text" inputMode="numeric" className="tabular" autoFocus
                                 aria-label={`Montant à retirer de ${category.name}`}
                                 value={withdrawal} placeholder="0"
                                 onChange={(e) => setWithdrawal(e.target.value)} />
                          <span>Ar</span>
                        </label>
                        <p>Cet argent revient parmi l'argent à répartir.</p>
                        <div className="withdraw-actions">
                          <button type="button" className="withdraw-go" onClick={() => {
                            const amount = parseAmountBounded(withdrawal) ?? 0
                            setWithdrawing(null); setWithdrawal('')
                            void withdrawFromSavings(walletId, category.id, amount)
                          }}>Retirer</button>
                          <button type="button" onClick={() => {
                            setWithdrawing(null); setWithdrawal('')
                          }}>Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="withdraw-open"
                              onClick={() => setWithdrawing(category.id)}>
                        Retirer de l'épargne
                      </button>
                    )
                  )}

                  {envelope && envelope.plan_mode === 'daily' && envelope.planned_days > 0 && (
                    <p className="envelope-progress">
                      <strong className="tabular">
                        {envelope.confirmed_days}/{envelope.planned_days}
                      </strong>{' '}
                      {envelope.planned_days > 1 ? 'journées confirmées' : 'journée confirmée'}
                    </p>
                  )}

                  {/* Ce qui a été pris dans l'enveloppe SANS passer par une
                      journée prévue. Sans cette ligne, le compte ne tombe pas
                      juste : « 150 000 d'enveloppe, une journée de 18 000
                      confirmée » et pourtant « 127 000 restants ». L'argent
                      était bien déduit — c'est l'explication qui manquait. */}
                  {envelope && !envelope.carries_over
                    && envelope.plan_mode !== 'none' && envelope.spentOffPlan > 0 && (
                    <p className="envelope-offplan">
                      dont <strong className="tabular">
                        {formatAriary(envelope.spentOffPlan)}
                      </strong>{' '}
                      {/* « En une fois » n'a pas de journées : elle a UNE
                          échéance. Une phrase qui décrit un mode que
                          l'enveloppe n'a pas se lit comme un bug. */}
                      {envelope.plan_mode === 'once'
                        ? "saisis hors de l'échéance prévue"
                        : 'saisis hors des journées prévues'}
                    </p>
                  )}

                  {envelope && (
                    <div className="envelope-actions">
                      {envelope.plan_mode === 'daily' && envelope.amount > 0 && (
                        <button type="button" className="envelope-plan"
                                onClick={() => setPlanning({ category, envelope })}>
                          <Icon name="list" size={16} /> Prévoir jour par jour
                        </button>
                      )}
                      {/* Toujours proposé, même sur une enveloppe encore vide :
                          un bouton qui change de place selon l'état est un
                          bouton qu'on ne retrouve pas. La feuille sait dire
                          qu'il n'y a rien à montrer. */}
                      <button type="button" className="envelope-history"
                              onClick={() => setHistory({ category, envelope })}>
                        <Icon name="history" size={16} /> Voir les opérations
                      </button>
                    </div>
                  )}

                  {/* Chaque option porte ce qu'elle FAIT, pas son nom technique.
                      Un choix à trois branches et non deux cases à cocher : les
                      modes s'excluent, et deux booléens laisseraient exister un
                      « les deux à la fois » qui n'a pas de sens. */}
                  <details className="envelope-options">
                    <summary>Options</summary>

                    <fieldset className="plan-modes">
                      <legend>Comment cette enveloppe se dépense</legend>
                      {PLANS.map((plan) => (
                        <label key={plan.mode}>
                          <input
                            type="radio"
                            name={`plan-${category.id}`}
                            checked={(envelope?.plan_mode ?? 'none') === plan.mode}
                            onChange={() => void choosePlan(category, envelope, plan.mode)}
                          />
                          <span>
                            <strong>{plan.titre}</strong>
                            {plan.aide}
                          </span>
                        </label>
                      ))}
                  </fieldset>

                  {/* Le cumul n'a de sens que pour une épargne. Partout ailleurs
                      il embrouille : une enveloppe de nourriture qui s'additionne
                      d'un mois sur l'autre ne veut rien dire, et la fin de cycle
                      est justement là pour décider du reste. */}
                  {category.icon === 'savings' && (
                    <label className="carry-option">
                      <input
                        type="checkbox"
                        checked={envelope?.carries_over ?? true}
                        onChange={(e) => void toggleCarry(category, envelope, e.target.checked)}
                      />
                      <span>
                        <strong>Cumuler d'un cycle à l'autre</strong>
                        L'argent mis de côté s'additionne et ne redevient
                        disponible que si tu le retires.
                      </span>
                    </label>
                  )}
                </details>
              </li>
            )
          })}
          </ul>
        </fieldset>
      </section>

      {planning && (
        <PlanDaysSheet
          walletId={walletId}
          category={planning.category}
          envelope={planning.envelope}
          range={range}
          onClose={() => setPlanning(null)}
          onOpen={onOpen}
        />
      )}

      {reporting && (
        <CycleReportSheet walletId={walletId} date={today()}
                          onClose={() => setReporting(false)} />
      )}

      {history && (
        <EnvelopeHistorySheet
          walletId={walletId}
          category={history.category}
          envelope={history.envelope}
          range={range}
          onClose={() => setHistory(null)}
          onOpen={onOpen}
        />
      )}

      {/* Le bilan se lit LÀ où l'on se pose la question : sur le budget du cycle
          qu'on est en train de vivre, pas dans un onglet à part. */}
      <button type="button" className="budget-report" onClick={() => setReporting(true)}>
        <Icon name="list" size={16} /> Voir le résumé de {cycleLabel(range, cycle)}
      </button>

      {/* Les objectifs, SOUS les enveloppes : on répartit d'abord ce qu'on doit
          dépenser, on met de côté ensuite. L'ordre de l'écran est l'ordre de la
          décision. */}
      <GoalsSection walletId={walletId} goals={summary.goals}
                    available={summary.toAllocate} />

      {/* Ce n'est PAS « des opérations sans catégorie » : c'est de l'argent
          dépensé dans une catégorie à laquelle rien n'a été réparti. Le dire
          autrement laisserait croire que remplir la catégorie suffit. */}
      {summary.outOfEnvelope > 0 && (
        <p className="out-of-envelope">
          <strong className="tabular">{formatAriary(summary.outOfEnvelope)}</strong> dépensés
          ce cycle dans des catégories auxquelles tu n'as rien réparti. Donne-leur
          un montant ci-dessus pour les suivre.
        </p>
      )}
    </div>
  )
}

/**
 * Enregistre l'ordre choisi et coupe le tri automatique.
 *
 * Au niveau du module et non dans le composant : le glisser se termine dans un
 * écouteur `window`, qui ne doit capturer aucune valeur du rendu en cours.
 */
async function persistOrder(walletId: string, next: Category[]): Promise<void> {
  const wallet = await db.wallets.get(walletId)
  if (wallet && !wallet.manual_category_order) {
    await writeLocal('wallets', {
      id: wallet.id, name: wallet.name, cycle: wallet.cycle,
      expected_income: wallet.expected_income ?? null,
      manual_category_order: true,
    })
  }
  // Seules les lignes qui bougent vraiment partent dans la file : réécrire les
  // onze catégories à chaque geste ferait onze opérations de synchronisation.
  for (const [position, category] of next.entries()) {
    if (category.position === position) continue
    await writeLocal('categories', { ...category, position })
  }
}

const PLANS: Array<{ mode: PlanMode; titre: string; aide: string }> = [
  {
    mode: 'none', titre: 'Au fil de l’eau',
    aide: 'Rien n’est prévu d’avance : tu saisis tes dépenses quand elles arrivent.',
  },
  {
    mode: 'once', titre: 'En une fois',
    aide: 'Une seule dépense à confirmer, posée tout de suite. Pour le loyer, ' +
          'l’école, une facture.',
  },
  {
    mode: 'daily', titre: 'Jour par jour',
    aide: 'Réparti sur les jours du cycle, à confirmer chaque jour. Pour la ' +
          'nourriture, le transport.',
  },
]

/** Distance au bord à partir de laquelle la liste défile toute seule. */
const EDGE = 72
/** Pixels par image, au plus. */
const SCROLL_SPEED = 14

interface Geometry {
  /** Position des lignes au départ, en coordonnées de contenu. */
  rows: Array<{ top: number; bottom: number }>
  /** Position du doigt au départ, en coordonnées de contenu. */
  startDoc: number
}

interface Drag {
  /** Déplacement vertical de la ligne tenue — exactement celui du doigt. */
  dy: number
  from: number
  to: number
  /** Hauteur libérée par la ligne tenue, écart compris. */
  shift: number
}

/** Déplace un élément d'un index à un autre, sans muter l'original. */
function move<T>(items: T[], from: number, to: number): T[] {
  if (from === to) return items
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}
